import type { Project, ProjectListItem } from '@atlas/api-contract';
import { FolderOpen, Plus, SearchX } from 'lucide-react';
import { useCallback, useState } from 'react';

import { PaginationBar } from '@/components/pagination-bar';
import { EmptyState, ErrorState, SkeletonGrid } from '@/components/states';
import { Button } from '@/components/ui/button';
import { useCan } from '@/features/auth';
import { cn } from '@/lib/utils';

import { CreateProjectDialog } from './create-project-dialog';
import { DeleteProjectDialog } from './delete-project-dialog';
import { EditProjectDialog } from './edit-project-dialog';
import { ProjectCard } from './project-card';
import { useProjectsQuery } from './projects-queries';
import {
  projectsSearchCodec,
  type ProjectSort,
  type ProjectsSearch,
} from './projects-search';
import { ProjectsToolbar } from './projects-toolbar';

export interface ProjectsBrowserProps {
  /** The URL's search state. The single source of truth for this view. */
  search: ProjectsSearch;
  /**
   * Write new search state back to the URL. `replace` is for changes the back
   * button should not step through one at a time — i.e. typing.
   */
  onSearchChange: (next: ProjectsSearch, replace?: boolean) => void;
}

/**
 * The project list (spec R6.5, R6.7).
 *
 * Every control writes to the URL and reads its value back from it, so a
 * reload, a bookmark and a pasted link all restore the same view — there is no
 * second copy of "which page am I on" to disagree with the address bar.
 *
 * All three list states are wired to the real query: a skeleton on the first
 * load, an error with a retry that refetches, and two different empty states,
 * because "no projects exist" and "your search matched nothing" need different
 * things from the reader.
 */
export const ProjectsBrowser = ({
  search,
  onSearchChange,
}: ProjectsBrowserProps) => {
  const resolved = projectsSearchCodec.resolve(search);
  const query = useProjectsQuery(search);

  const can = useCan();
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState<ProjectListItem | null>(null);

  /*
   * Creating is a Designer/Admin action (spec matrix). The button is hidden for
   * a Commenter as a convenience; the API is the real gate (spec R7.8).
   */
  const openCreate = useCallback(() => {
    setCreateOpen(true);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      // A new filter invalidates the current page number, so reset to the first.
      onSearchChange(
        projectsSearchCodec.parse({ ...search, search: value, page: 1 }),
        true,
      );
    },
    [onSearchChange, search],
  );

  const handleSortChange = useCallback(
    (value: ProjectSort) => {
      onSearchChange(
        projectsSearchCodec.parse({ ...search, sort: value, page: 1 }),
      );
    },
    [onSearchChange, search],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      onSearchChange(projectsSearchCodec.parse({ ...search, page }));
    },
    [onSearchChange, search],
  );

  const goToFirstPage = useCallback(() => {
    handlePageChange(1);
  }, [handlePageChange]);

  const isFiltered = resolved.search !== '';

  const renderList = () => {
    if (query.isPending) {
      return <SkeletonGrid count={6} label="Loading projects" />;
    }

    if (query.isError) {
      return (
        <ErrorState
          title="Could not load projects"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      );
    }

    const { items, total } = query.data;

    if (items.length === 0) {
      if (isFiltered) {
        return (
          <EmptyState
            icon={SearchX}
            title={`No projects match “${resolved.search}”`}
            description="Search covers project names and descriptions. Try a shorter term."
            action={
              <Button
                variant="outline"
                onClick={() => {
                  handleSearchChange('');
                }}
              >
                Clear search
              </Button>
            }
          />
        );
      }

      if (total > 0) {
        return (
          <EmptyState
            icon={FolderOpen}
            title="This page is empty"
            description={`There are ${String(total)} projects, but none on page ${String(resolved.page)}.`}
            action={
              <Button variant="outline" onClick={goToFirstPage}>
                Go to the first page
              </Button>
            }
          />
        );
      }

      return (
        <EmptyState
          icon={FolderOpen}
          title="No projects yet"
          description={
            can.createProject
              ? 'A project groups the prototypes for one piece of work. Create the first one to start uploading and reviewing.'
              : 'A project groups the prototypes for one piece of work. Nothing has been created yet.'
          }
          action={
            can.createProject ? (
              <Button onClick={openCreate}>
                <Plus aria-hidden="true" />
                New project
              </Button>
            ) : undefined
          }
        />
      );
    }

    return (
      <>
        <div
          className={cn(
            'grid grid-cols-1 gap-4 transition-opacity sm:grid-cols-2 xl:grid-cols-3',
            // Paging and filtering keep the old cards on screen; dim them so it
            // is obvious they are the previous answer, not the new one.
            query.isFetching && 'opacity-60',
          )}
        >
          {items.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={setEditing}
              onDelete={setDeleting}
            />
          ))}
        </div>

        <PaginationBar
          page={resolved.page}
          pageSize={resolved.pageSize}
          total={total}
          onPageChange={handlePageChange}
          itemNoun="projects"
        />
      </>
    );
  };

  return (
    <div className="space-y-6">
      <ProjectsToolbar
        search={resolved.search}
        sort={resolved.sort}
        onSearchChange={handleSearchChange}
        onSortChange={handleSortChange}
        onCreate={openCreate}
        canCreate={can.createProject}
        isFetching={query.isFetching}
      />

      {renderList()}

      <CreateProjectDialog open={isCreateOpen} onOpenChange={setCreateOpen} />

      <EditProjectDialog
        project={editing}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null);
          }
        }}
      />

      <DeleteProjectDialog
        project={deleting}
        onOpenChange={(open) => {
          if (!open) {
            setDeleting(null);
          }
        }}
      />
    </div>
  );
};
